import { useEffect, useState } from "react";
import DialogInOne from "../../../../components/DialogInOne";
import { setUpdateExchangeRate, useReduxContext } from "../reduxContext";
import GridBox from "../../../../components/GridBox";
import MpTextField from "../../../../components/MpTextField";
import { useAlerting, useTranslation, useYubiPrompt } from "../../../../hooks";
import { updateCrossMerchantTransferExchangeRate } from "../../../../api/merchant";
import YubiPrompter from "../../../../components/Prompter";
import { SelectChangeEvent } from "../../../../components/MuiGenerals";
import { useAppSelector } from "../../../../reducer/hooks";
import { selectCrossMerchant } from "../../../../reducer/crossMerchantSlice";
import { findChainInfo } from "../../../../utils/helper";
import { translateKeyObj as TK, translatePrefix } from "../config";
import { appError } from "../../../../utils/errorHandler";

interface Ifields {
  exchange_rate: string;
  external_reference: string;
}

const initFields: Ifields = {
  exchange_rate: "",
  external_reference: "",
};

const UpdateExchangeRateDialog = () => {
  const { t } = useTranslation(translatePrefix);
  const { alerting } = useAlerting();

  const [fields, setFields] = useState(initFields);
  const [state, dispatch] = useReduxContext();

  const {
    id: path_id = "",
    from_chain_id = "",
    to_chain_id = "",
    from_merchant_id = "",
    from_chain_type = "",
    to_merchant_id = "",
    from_asset_name,
    to_asset_name,
    liquidity_provider,
    liquidity_provider_merchant_id,
    to_chain_type = "",
    cross_merchant_transfer_exchange_rate,
  } = state.updateExchangeRate ?? {};

  const exchangeRate = cross_merchant_transfer_exchange_rate?.exchange_rate;

  useEffect(() => {
    if (!exchangeRate) return;
    setFields((fields) => ({ ...fields, ["exchange_rate"]: exchangeRate }));
  }, [exchangeRate]);

  const { merchantMapping } = useAppSelector(selectCrossMerchant);

  const { getResAfterYubi, prompterConfig } = useYubiPrompt();

  const onChange =
    (type: keyof Ifields) =>
    (
      e:
        | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        | SelectChangeEvent<string>
    ) => {
      const value = e.target.value;
      setFields((fields) => ({ ...fields, [type]: value }));
    };

  const { name: to_chain_name } =
    findChainInfo(to_chain_type, to_chain_id) || {};

  const { name: from_chain_name } =
    findChainInfo(from_chain_type, from_chain_id) || {};

  const getMerchantName = (merchantId: string) => merchantMapping[merchantId];

  const genBoxData: Array<[string, JSX.Element]> = [
    [TK.merchantFrom, <>{getMerchantName(from_merchant_id)}</>],
    [TK.merchantTo, <>{getMerchantName(to_merchant_id)}</>],
    [TK.chainNameFrom, <>{from_chain_name}</>],
    [TK.chainNameTo, <>{to_chain_name}</>],
    [TK.currencyFrom, <>{from_asset_name}</>],
    [TK.currencyTo, <>{to_asset_name}</>],
    [
      TK.merchantLp,
      <>{getMerchantName(liquidity_provider_merchant_id || "")}</>,
    ],
    [TK.liquidityProvider, <>{liquidity_provider}</>],
    [
      TK.exchangeRate,
      <MpTextField
        label={t(TK.phExchangeRate)}
        defaultValue={exchangeRate}
        onChange={onChange("exchange_rate")}
      />,
    ],
    [
      TK.externalReference,
      <MpTextField
        label={t(TK.phExternalReference)}
        value={fields.external_reference}
        onChange={onChange("external_reference")}
      />,
    ],
  ];

  const handleOnConfirm = async () => {
    if (exchangeRate === fields.exchange_rate) {
      return alerting("warning", t(TK.noExchangeRateUpdate));
    }

    const mandatoryFieldsEntries = Object.entries(fields);

    const emptyFieldsEntries = mandatoryFieldsEntries.filter(
      ([_key, value]) => !value
    );

    const firstEmptyFieldEntries: [key: string, value: string] =
      emptyFieldsEntries[0];

    if (firstEmptyFieldEntries) {
      const fieldForDisplay = t(firstEmptyFieldEntries[0]);
      return alerting(
        "warning",
        t(TK.createWarning, { verb: t(TK.enter), field: fieldForDisplay })
      );
    }

    const res = await getResAfterYubi(
      updateCrossMerchantTransferExchangeRate,
      {
        liquidityProviderMerchantId: Number(liquidity_provider_merchant_id),
        pathId: Number(path_id),
        exchangeRate: fields.exchange_rate,
        externalRef: fields.external_reference,
      },
      {
        isSkipErrorHandling: true,
      }
    );

    if ((res as any).error?.code === -51) {
      return alerting("warning", t(TK.referenceAlreadyExisted));
    }

    if (!res || (res as any).error) {
      return appError({
        ...(res as any).error,
        requestId: (res as any).requestId,
      });
    }

    alerting("success", t(TK.updateExchangeRateSuccess));
    handleOnClose();
  };

  const handleOnClose = () => {
    dispatch(setUpdateExchangeRate(undefined));
    setFields(initFields);
  };

  const dialogConfig = {
    title: t(TK.updateExchangeRate),
    self: {
      open: state.updateExchangeRate !== undefined,
      onClose: handleOnClose,
    },
    content: (
      <GridBox
        translatePrefix={translatePrefix}
        data={genBoxData}
        itemSx={{ flexWrap: "nowrap" }}
      />
    ),
    onCancel: handleOnClose,
    onConfirm: handleOnConfirm,
  } as const;

  return (
    <>
      <YubiPrompter {...prompterConfig} />
      <DialogInOne
        {...dialogConfig}
        rest={{
          maxWidth: "md",
          closeAfterTransition: false,
        }}
      />
    </>
  );
};

export default UpdateExchangeRateDialog;
